import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  apiUsersmegenerateMfaEmailAuthCodeFormatPostMutation,
  apiUsersmegenerateMfaGoogleTotpFormatPostMutation,
} from "@/generated/api/@tanstack/react-query.gen";
import { siteConfig } from "@/config/site.ts";
import { EMfaMethod } from "@/lib/constants.ts";
import { cn } from "@/lib/utils.ts";
import {
  CardAuth,
  CardAuthContent,
  CardAuthDescription,
  CardAuthFooter,
  CardAuthHeader,
  CardAuthTitle,
} from "@/components/ui/card-auth.tsx";
import { SettingMfaAuthenticatorForm } from "@/app/(guest)/setting-mfa/_components/setting-mfa-authenticator-form.tsx";
import { SettingMfaEmailForm } from "@/app/(guest)/setting-mfa/_components/setting-mfa-email-form.tsx";
import MFASelection from "@/app/(guest)/setting-mfa/_components/setting-mfa-method-selector.tsx";
import {
  MfaMethod,
  MfaStep,
} from "@/app/(guest)/setting-mfa/_data/setting-mfa-schema.ts";

export const Route = createFileRoute("/(guest)/setting-mfa/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [step, setStep] = useState<MfaStep>(1);
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>();
  const [qrCode, setQrCode] = useState<string>("");
  const [secretCode, setSecretCode] = useState<string>("");

  // Mutation for Setting Multi-Factor Authentication with Authenticator Application
  const { mutate: generateMfaGoogleTOTP } = useMutation({
    ...apiUsersmegenerateMfaGoogleTotpFormatPostMutation(),
    onSuccess: (data) => {
      const qrCodeUri = `otpauth://totp/${siteConfig.title}:${data.identifier}?secret=${data.totpSecret}`;
      setQrCode(qrCodeUri);
      setSecretCode(data?.totpSecret ?? "Something went wrong");
      setStep(2);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Mutation for Setting Multi-Factor Authentication with Email Authentication Code
  const { mutate: generateMfaEmail } = useMutation({
    ...apiUsersmegenerateMfaEmailAuthCodeFormatPostMutation(),
    onSuccess: () => {
      setStep(2);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const onSelectMethod = (method: MfaMethod) => {
    setMfaMethod(method);
    switch (method) {
      case EMfaMethod.AUTHENTICATOR:
        return generateMfaGoogleTOTP({});
      case EMfaMethod.EMAIL:
        return generateMfaEmail({ body: {} });
      default:
        toast.error("Something went wrong");
        return;
    }
  };

  return (
    <CardAuth>
      <CardAuthHeader>
        <CardAuthTitle>Multi Factor Authentication</CardAuthTitle>
        <CardAuthDescription>Enhance your account security</CardAuthDescription>
      </CardAuthHeader>
      <CardAuthContent className="max-w-md">
        {step === 1 && <MFASelection onSelectMethod={onSelectMethod} />}
        {step === 2 &&
          (mfaMethod === EMfaMethod.AUTHENTICATOR ? (
            <SettingMfaAuthenticatorForm
              qrCode={qrCode}
              secretCode={secretCode}
              onBack={() => setStep(1)}
            />
          ) : (
            <SettingMfaEmailForm onBack={() => setStep(1)} />
          ))}
      </CardAuthContent>
      <CardAuthFooter
        className={cn("flex items-center justify-center", {
          hidden: step === 2,
        })}
      >
        <Link
          to="/login"
          className="text-primary flex items-center text-sm hover:underline"
        >
          <ArrowLeft className="mr-2 size-4" />
          Back to Login
        </Link>
      </CardAuthFooter>
    </CardAuth>
  );
}
